import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HighlightAltIcon from '@mui/icons-material/HighlightAlt';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import { useFullscreenContent } from './FullscreenContentContext';

const MIN_FONT_SIZE = 14;
const MAX_FONT_SIZE = 30;

const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const paginateByFontSize = (rawContent, fontSize) => {
  const normalized = (rawContent || '').replace(/\r\n/g, '\n').trim();
  if (!normalized) return [];

  // Larger fonts get fewer chars per page to preserve a book-like page flip flow.
  const charLimit = Math.max(700, Math.round(1700 * (16 / fontSize)));
  const paragraphs = normalized.split(/\n{2,}/).map((part) => part.trim()).filter(Boolean);

  const pages = [];
  let current = '';

  paragraphs.forEach((paragraph) => {
    const block = `${paragraph}\n\n`;
    if ((current + block).length > charLimit && current.trim().length > 0) {
      pages.push(current.trim());
      current = block;
      return;
    }
    current += block;
  });

  if (current.trim().length > 0) {
    pages.push(current.trim());
  }

  return pages;
};

const renderHighlightedText = (text, highlights) => {
  if (!text) return '';
  if (!highlights || highlights.length === 0) return text;

  const activeHighlights = highlights
    .map((item) => item.trim())
    .filter((item) => item.length >= 2);

  if (activeHighlights.length === 0) return text;

  const pattern = activeHighlights
    .sort((a, b) => b.length - a.length)
    .map((item) => escapeRegex(item))
    .join('|');

  if (!pattern) return text;

  const chunks = text.split(new RegExp(`(${pattern})`, 'gi'));

  return chunks.map((chunk, index) => {
    if (!chunk) return null;
    const matched = activeHighlights.some((item) => item.toLowerCase() === chunk.toLowerCase());
    if (!matched) {
      return <React.Fragment key={`txt-${index}`}>{chunk}</React.Fragment>;
    }

    return (
      <mark
        key={`hl-${index}`}
        style={{
          backgroundColor: '#ffe17a',
          color: '#1f2937',
          padding: '0 2px',
          borderRadius: 3,
        }}
      >
        {chunk}
      </mark>
    );
  });
};

const isSingleWord = (value) => /^[A-Za-z][A-Za-z'\-]*$/.test(value || '');

export default function KindleReaderContent({ content, contentId }) {
  const isFullscreen = useFullscreenContent();
  const [fontSize, setFontSize] = useState(18);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedText, setSelectedText] = useState('');
  const [selectedWord, setSelectedWord] = useState('');
  const [meaningState, setMeaningState] = useState({ loading: false, error: '', definitions: [] });
  const [highlights, setHighlights] = useState({});

  const pages = useMemo(() => paginateByFontSize(content, fontSize), [content, fontSize]);
  const storageKey = useMemo(() => `lms_reader_highlights_${contentId || 'unknown'}`, [contentId]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          setHighlights(parsed);
          return;
        }
      }
    } catch (error) {
      console.warn('Failed to read highlights from storage:', error);
    }
    setHighlights({});
  }, [storageKey]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(highlights));
    } catch (error) {
      console.warn('Failed to save highlights:', error);
    }
  }, [highlights, storageKey]);

  useEffect(() => {
    if (currentPage > pages.length - 1) {
      setCurrentPage(0);
    }
  }, [currentPage, pages.length]);

  useEffect(() => {
    if (!selectedWord) {
      setMeaningState({ loading: false, error: '', definitions: [] });
      return;
    }

    const lookupMeaning = async () => {
      setMeaningState({ loading: true, error: '', definitions: [] });
      try {
        const response = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${selectedWord}`);
        if (!response.ok) {
          throw new Error('Meaning not found for this word.');
        }

        const payload = await response.json();
        const firstEntry = payload?.[0];
        const definitions = [];

        (firstEntry?.meanings || []).forEach((meaning) => {
          (meaning.definitions || []).slice(0, 2).forEach((def) => {
            definitions.push(`${meaning.partOfSpeech || 'word'}: ${def.definition}`);
          });
        });

        setMeaningState({
          loading: false,
          error: definitions.length === 0 ? 'No definition available.' : '',
          definitions: definitions.slice(0, 4),
        });
      } catch (error) {
        setMeaningState({
          loading: false,
          error: error.message || 'Failed to fetch meaning.',
          definitions: [],
        });
      }
    };

    lookupMeaning();
  }, [selectedWord]);

  const pageHighlights = highlights[String(currentPage)] || [];
  const pageText = pages[currentPage] || '';

  const handleSelection = () => {
    const text = window.getSelection?.()?.toString()?.trim() || '';
    if (!text) return;

    setSelectedText(text);
    if (isSingleWord(text)) {
      setSelectedWord(text.toLowerCase());
    }
  };

  const clearSelection = () => {
    setSelectedText('');
    window.getSelection?.()?.removeAllRanges?.();
  };

  const addHighlight = () => {
    const trimmed = selectedText.trim();
    if (!trimmed) return;

    setHighlights((prev) => {
      const key = String(currentPage);
      const existing = prev[key] || [];
      if (existing.some((item) => item.toLowerCase() === trimmed.toLowerCase())) {
        return prev;
      }

      return {
        ...prev,
        [key]: [...existing, trimmed],
      };
    });

    clearSelection();
  };

  const clearPageHighlights = () => {
    setHighlights((prev) => {
      const next = { ...prev };
      delete next[String(currentPage)];
      return next;
    });
  };

  if (!content || content.trim().length === 0) {
    return <Alert severity="info">No notes available for this lesson.</Alert>;
  }

  return (
    <Stack
      spacing={2}
      sx={{
        height: isFullscreen ? '100%' : 'auto',
        minHeight: isFullscreen ? 0 : undefined,
        flex: isFullscreen ? 1 : undefined,
      }}
    >
      <Paper elevation={0} variant="outlined" sx={{ p: 1.5, borderRadius: 2, flexShrink: 0 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <AutoStoriesIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={700}>
              Reader Mode
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Zoom out">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setFontSize((prev) => Math.max(MIN_FONT_SIZE, prev - 2))}
                  disabled={fontSize <= MIN_FONT_SIZE}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Chip size="small" label={`${fontSize}px`} />
            <Tooltip title="Zoom in">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setFontSize((prev) => Math.min(MAX_FONT_SIZE, prev + 2))}
                  disabled={fontSize >= MAX_FONT_SIZE}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Box sx={{ flex: 1 }} />
          <Chip size="small" color="primary" variant="outlined" label={`Page ${pages.length === 0 ? 0 : currentPage + 1} / ${pages.length}`} />
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 4 },
          flex: isFullscreen ? 1 : undefined,
          minHeight: isFullscreen ? 0 : 320,
          borderRadius: 2,
          background: 'linear-gradient(180deg, #fffdf8 0%, #fff9ec 100%)',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          variant="body1"
          sx={{
            whiteSpace: 'pre-wrap',
            lineHeight: 2,
            fontSize: `${fontSize}px`,
            color: '#242424',
          }}
        >
          {renderHighlightedText(pageText, pageHighlights)}
        </Typography>
      </Paper>

      <Stack
        direction="row"
        spacing={1}
        justifyContent="space-between"
        alignItems="center"
        sx={{ flexShrink: 0 }}
      >
        <Button
          variant="outlined"
          startIcon={<NavigateBeforeIcon />}
          onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
          disabled={currentPage === 0}
        >
          Previous Page
        </Button>
        <Button
          variant="outlined"
          endIcon={<NavigateNextIcon />}
          onClick={() => setCurrentPage((prev) => Math.min(pages.length - 1, prev + 1))}
          disabled={currentPage >= pages.length - 1}
        >
          Next Page
        </Button>
      </Stack>

      {selectedText && (
        <Alert
          severity="info"
          action={
            <Button size="small" color="inherit" startIcon={<HighlightAltIcon />} onClick={addHighlight}>
              Highlight
            </Button>
          }
        >
          Selected: "{selectedText.slice(0, 120)}{selectedText.length > 120 ? '...' : ''}"
        </Alert>
      )}

      {pageHighlights.length > 0 && (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {pageHighlights.length} highlight{pageHighlights.length > 1 ? 's' : ''} on this page
          </Typography>
          <Button size="small" color="warning" onClick={clearPageHighlights}>
            Clear Page Highlights
          </Button>
        </Stack>
      )}

      {selectedWord && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Word Meaning: {selectedWord}
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          {meaningState.loading && <Typography variant="body2">Loading meaning...</Typography>}
          {!meaningState.loading && meaningState.error && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>{meaningState.error}</Alert>
          )}
          {!meaningState.loading && meaningState.definitions.length > 0 && (
            <Stack spacing={0.8}>
              {meaningState.definitions.map((definition, index) => (
                <Typography key={`def-${index}`} variant="body2">
                  {index + 1}. {definition}
                </Typography>
              ))}
            </Stack>
          )}
        </Paper>
      )}
    </Stack>
  );
}
